import { EllipseCurve, Matrix4, Vector3 } from "three";
import { Config } from "../../Config/Config.js";
import { bindedModelBase } from "../../Models/BindedModel.js";
import { Consts } from "../../Utils/Consts.js";
import { getEllipseIntersectionWithRotation } from "../../Utils/IntersectionUtils.js";
import { VectorUtils } from "../../Utils/VectorUtils.js";
import { CalculationPoint } from "../Primitive/CalculationPoint.js";
import { CalibrationRing } from "./CalibrationRing.js";
import { CalibrationShapeBase } from "./CalibrationShapeBase.js";
import { CalibrationMode } from "./CalibrationTool.js";
import { DraggableCone } from "./DraggableCone.js";
import { DraggableCone2 } from "./DraggableCone2.js";
/**
 * Create an ellipse registering 3 points. Used to calibrate.
 */
export class CalibrationEllipse extends CalibrationShapeBase {
    constructor(viewType) {
        super(viewType);
        this.mode = CalibrationMode.ellipse;
        this.pointsToRegister = 3;
        this.center = new Vector3();
        this._curveComplexity = 50;
        this._ellipseCurve = new EllipseCurve(0, 0, 0, 0, 0, 2 * Math.PI, false, 0);
        this._startDragMatrix = new Matrix4();
        this._rotationPointLocalAngle = 0; // Angolo nel frame locale dell'ellisse (indipendente da rotation)
        this.translateZ(200);
        this.add(this.ring = new CalibrationRing(viewType), this.rotationPoint = new CalculationPoint('axialRotationPoint'), this.coneA = new DraggableCone(Config.calib_cone_color, Config.calib_cone_radius, Config.calib_cone_height), //sostituire? controllare la rot NaN
        this.coneB = new DraggableCone(Config.calib_cone_color, Config.calib_cone_radius, Config.calib_cone_height), this.coneC = new DraggableCone2(Config.calib_cone_color, Config.calib_cone_radius, Config.calib_cone_height));
        this.coneA.bindProperty("visible", (m) => { return m.garageRingLoaded && !m[this._isRegisteringClickKey]; }, ["garageRingLoaded", this._isRegisteringClickKey]);
        this.coneB.bindProperty("visible", (m) => { return m.garageRingLoaded && !m[this._isRegisteringClickKey]; }, ["garageRingLoaded", this._isRegisteringClickKey]);
        this.coneC.bindProperty("visible", (m) => { return m.garageRingLoaded && !m[this._isRegisteringClickKey]; }, ["garageRingLoaded", this._isRegisteringClickKey]);
        this.coneA.bindEvent('onDragStart', () => this.saveRotationPointLocalAngle());
        this.coneB.bindEvent('onDragStart', () => this.saveRotationPointLocalAngle());
        this.coneA.bindEvent("onDragMove", (e) => {
            const oldXRadius = this._ellipseCurve.xRadius;
            this.updateEllipseParameters(e.position, this.coneB.position);
            e.preventDefault = !this.isRadiusValid(e.position, this.coneB.position);
            if (e.preventDefault)
                return;
            this.updateConeC(e.position, this.coneB.position, oldXRadius);
            this.attachRotationPointToEllipse();
        });
        this.coneB.bindEvent("onDragMove", (e) => {
            const oldXRadius = this._ellipseCurve.xRadius;
            this.updateEllipseParameters(this.coneA.position, e.position);
            e.preventDefault = !this.isRadiusValid(e.position, this.coneA.position);
            if (e.preventDefault)
                return;
            this.updateConeC(e.position, this.coneA.position, oldXRadius);
            this.attachRotationPointToEllipse();
        });
        this.coneC.bindEvent("onDragMove", (e) => {
            e.preventDefault = true;
            const xRadius = this.center.distanceTo(this.coneA.position);
            const dir = VectorUtils.getPerpendicularWithDirectionBasedOnPoint(this.coneA.position, this.center, e.position);
            const projectedPosition = VectorUtils.projectOnVector(e.position, this.center.clone().add(dir), this.center);
            const yRadius = Math.min(xRadius, projectedPosition.distanceTo(this.center));
            const isMastertabBehind = !VectorUtils.arePointsOnSameSide(this.center, this.coneA.position, Consts.planeNormal, this.rotationPoint.position, this.coneC.position);
            this.coneC.position.addVectors(this.center, dir.setLength(yRadius));
            const dirRefPoint = !isMastertabBehind ? this.coneC.position : this.center.clone().sub(this.coneC.position).add(this.center);
            this.updateMastertab(this.rotationPoint.position, yRadius, dirRefPoint);
        });
        this.ring.mastertab.bindEvent("onDragMove", (e) => {
            e.position.applyMatrix4(this._startDragMatrix);
            this.updateMastertabByDrag(e.position);
        });
        this.ring.mastertab.bindEvent("onDragStart", (e) => {
            this._startDragMatrix.copy(this.ring.matrixWorld);
        });
        this.bindEvent("onDragEnd", () => {
            if (bindedModelBase.garageRingLoaded) {
                this.scaleCones();
            }
        });
        this.bindEvent("onAfterDragMove", () => this.update());
    }
    bindProperties() {
        this.A.bindProperty("visible", (m) => { return !m.garageRingLoaded && !m[this._isRegisteringClickKey]; }, ["garageRingLoaded", this._isRegisteringClickKey]);
        this.B.bindProperty("visible", (m) => { return !m.garageRingLoaded && !m[this._isRegisteringClickKey]; }, ["garageRingLoaded", this._isRegisteringClickKey]);
        this.C.bindProperty("visible", (m) => { return !m.garageRingLoaded && !m[this._isRegisteringClickKey]; }, ["garageRingLoaded", this._isRegisteringClickKey]);
    }
    bindEvents() {
        // avoid to bind shapeBase events, necessary because we can't stop propagation
        this.A.bindEvent("onDragMove", (e) => {
            this.updateEllipseParameters(e.position, this.B.position);
            e.preventDefault = !this.isRadiusValid(e.position, this.B.position);
        });
        this.B.bindEvent("onDragMove", (e) => {
            this.updateEllipseParameters(this.A.position, e.position);
            e.preventDefault = !this.isRadiusValid(e.position, this.A.position);
        });
        this.C.bindEvent("onDragMove", (e) => {
            this.updateEllipseParameters(this.A.position, this.B.position); // capire se serve
            e.preventDefault = !this.isRadiusValid(this.A.position, this.B.position, e.position);
        });
    }
    isRadiusValid(position, oppositePosition, thirdPoint) {
        const radiusX = position.distanceTo(oppositePosition) / 2;
        if (!thirdPoint)
            return radiusX <= 50;
        const radiusY = this.getEllipseYRadius(thirdPoint);
        return !isNaN(radiusY) && radiusX > radiusY && radiusX <= 50; // 100 is plane width
    }
    updateConeC(position, oppositePosition, oldXRadius) {
        const center = oppositePosition.clone().lerp(position, 0.5);
        const xRadius = center.distanceTo(position);
        const dir = VectorUtils.getPerpendicularWithDirectionBasedOnPoint(position, center, this.coneC.position);
        let yRadius;
        if (oldXRadius === undefined) {
            const projectedPosition = VectorUtils.projectOnVector(this.rotationPoint.position, center, this.coneA.position); //todo usare funzione già presente per calcoalre yRadius
            const xDistance = projectedPosition.distanceTo(center);
            const angle = Math.acos(xDistance / xRadius);
            yRadius = this.rotationPoint.position.distanceTo(projectedPosition) / Math.sin(angle);
        }
        else {
            yRadius = (xRadius / oldXRadius) * this._ellipseCurve.yRadius;
        }
        this.coneC.position.addVectors(center, dir.setLength(yRadius));
    }
    updateMastertab(position, yRadius, dirRefPoint) {
        const projectedPosition = VectorUtils.projectOnVector(position, this.center, this.coneA.position);
        const xDir = projectedPosition.clone().sub(this.center);
        const yDir = VectorUtils.getPerpendicularWithDirectionBasedOnPoint(this.coneA.position, this.center, dirRefPoint);
        const xDistance = Math.min(this._ellipseCurve.xRadius, projectedPosition.distanceTo(this.center));
        const angle = Math.acos(xDistance / this._ellipseCurve.xRadius);
        const yDistance = Math.sin(angle) * yRadius;
        this.rotationPoint.position.copy(this.center).add(xDir.setLength(xDistance)).add(yDir.setLength(yDistance));
    }
    updateMastertabByDrag(position) {
        const ellipse = this._ellipseCurve;
        const int = getEllipseIntersectionWithRotation(this.center, ellipse.xRadius, ellipse.yRadius, ellipse.aRotation, position); // check se clockwise
        if (!int)
            return;
        this.rotationPoint.position.copy(int);
    }
    getMastertabAngle(position) {
        const projectedPosition = VectorUtils.projectOnVector(position, this.center, this.coneA.position);
        const xDistance = projectedPosition.distanceTo(this.center);
        return Math.acos(xDistance / this._ellipseCurve.xRadius);
    }
    update() {
        if (bindedModelBase.garageRingLoaded) {
            this.A.position.copy(this.coneA.position);
            this.B.position.copy(this.coneB.position);
            this.C.position.copy(this.coneC.position);
            this.rotateCones();
            this.scaleMastertab();
        }
        else {
            this.coneA.position.copy(this.A.position);
            this.coneB.position.copy(this.B.position);
            this.rotationPoint.position.copy(this.C.position);
            this.updateConeC(this.coneA.position, this.coneB.position);
        }
        this.updateEllipse();
        bindedModelBase.garageRingLoaded && this.updateRing();
    }
    rotateCones() {
        const angle = VectorUtils.linesAngleFromOrigin(Consts.horDir, this.coneA.position.clone().sub(this.coneB.position));
        this.coneA.setRotationFromAxisAngle(Consts.planeNormal, angle - Math.PI / 2);
        this.coneB.setRotationFromAxisAngle(Consts.planeNormal, angle + Math.PI / 2);
        this.coneC.setRotationFromAxisAngle(Consts.planeNormal, angle);
    }
    scaleCones() {
        const scale = this.A.position.distanceTo(this.B.position) / this.ring.diameter * 1.2;
        this.coneA.scale.setScalar(scale);
        this.coneB.scale.setScalar(scale);
        this.coneC.scale.setScalar(scale);
    }
    scaleMastertab() {
        const scale = this.A.position.distanceTo(this.B.position) / this.ring.diameter * 1.2;
        this.rotationPoint.scale.setScalar(scale);
    }
    updateEllipse() {
        this.updateEllipseParameters(this.A.position, this.B.position);
        const yRadius = this.getEllipseYRadius(this.C.position);
        if (!isNaN(yRadius)) {
            this._ellipseCurve.yRadius = Math.min(yRadius, yRadius); // TODO ??
            this._shape.setGeoPoints(this._ellipseCurve.getPoints(this._curveComplexity));
        }
    }
    updateRing() {
        const scale = this.coneA.position.distanceTo(this.coneB.position) / this.ring.diameter;
        this.ring.update(this.center, scale, -this._ellipseCurve.aRotation, this.getFrontal(), this.getAxial());
    }
    attachRotationPointToEllipse() {
        // Mantiene continuita` quando l'angolo dell'ellisse attraversa la discontinuita`:
        // i punti parametrizzati con t e t + PI sono equivalenti per la curva, ma opposti nello spazio.
        const ellipse = this._ellipseCurve;
        const rotCos = Math.cos(ellipse.aRotation);
        const rotSin = Math.sin(ellipse.aRotation);
        const prev = this.rotationPoint.position.clone();
        const getPointFromAngle = (angle) => {
            const localX = ellipse.xRadius * Math.cos(angle);
            const localY = ellipse.yRadius * Math.sin(angle);
            const globalX = localX * rotCos - localY * rotSin;
            const globalY = localX * rotSin + localY * rotCos;
            return new Vector3(this.center.x + globalX, this.center.y + globalY, prev.z);
        };
        const angle = this._rotationPointLocalAngle;
        const candidateA = getPointFromAngle(angle);
        const candidateB = getPointFromAngle(angle + Math.PI);
        // Sceglie il candidato piu` vicino alla posizione corrente per evitare "ribaltamenti".
        const useB = candidateB.distanceToSquared(prev) < candidateA.distanceToSquared(prev);
        const chosenPoint = useB ? candidateB : candidateA;
        this.rotationPoint.position.copy(chosenPoint);
    }
    saveRotationPointLocalAngle() {
        // Salva l'angolo parametrico del rotationPoint nel frame locale dell'ellisse
        const relativePos = this.rotationPoint.position.clone().sub(this.center);
        // Ruota indietro per ottenere le coordinate locali (non considerate la rotazione dell'ellisse)
        const cos = Math.cos(-this._ellipseCurve.aRotation);
        const sin = Math.sin(-this._ellipseCurve.aRotation);
        const localX = relativePos.x * cos - relativePos.y * sin;
        const localY = relativePos.x * sin + relativePos.y * cos;
        // Calcola l'angolo parametrico considerando i raggi dell'ellisse
        this._rotationPointLocalAngle = Math.atan2(localY / this._ellipseCurve.yRadius, localX / this._ellipseCurve.xRadius);
    }
    getFrontal() {
        const normal = VectorUtils.getPerpendicularWithDirectionBasedOnPoint(this.coneA.position, this.coneB.position, this.center.clone().sub(Consts.verDir));
        const sign = VectorUtils.computeSign(this.coneC.position, this.center, normal);
        return Math.asin(Math.min(1, this._ellipseCurve.yRadius / this._ellipseCurve.xRadius)) * sign;
    }
    getAxial() {
        const AB = this.coneA.position.clone().sub(this.coneB.position); // cache?
        const normal = VectorUtils.arePointsOnSameSide(this.center, this.center.clone().add(Consts.verDir), Consts.planeNormal, this.coneA.position, this.center.clone().add(Consts.horDir)) ? AB.multiplyScalar(-1) : AB;
        const projection = this.rotationPoint.position.clone().sub(this.center).projectOnVector(AB);
        const isMastertabBehind = !VectorUtils.arePointsOnSameSide(this.center, this.coneA.position, Consts.planeNormal, this.rotationPoint.position, this.coneC.position);
        const sign = VectorUtils.computeSign(this.rotationPoint.position, this.center, normal);
        const angle = Math.asin(Math.min(1, projection.length() / this._ellipseCurve.xRadius));
        return (isMastertabBehind ? Math.PI - angle : angle) * sign;
    }
    updateEllipseParameters(A, B) {
        this.center.lerpVectors(A, B, 0.5);
        this._ellipseCurve.aX = this.center.x;
        this._ellipseCurve.aY = this.center.y;
        this._ellipseCurve.xRadius = A.distanceTo(this.center);
        const rotation = VectorUtils.linesAngleFromOrigin(Consts.horDir, A.clone().sub(B));
        this._ellipseCurve.aRotation = Math.abs(rotation) > Math.PI / 2 ? (Math.PI - Math.abs(rotation)) * -Math.sign(rotation) : rotation; //testare 4 casi
    }
    getEllipseYRadius(point) {
        const rx = this._ellipseCurve.xRadius;
        const angle = this._ellipseCurve.aRotation;
        const delta = point.clone().sub(this.center);
        const ry = rx * Math.abs(delta.x * Math.sin(angle) - delta.y * Math.cos(angle)) * Math.sqrt(1 / (rx ** 2 - (delta.x * Math.cos(angle) + delta.y * Math.sin(angle)) ** 2));
        return ry;
    }
    reset() {
        bindedModelBase.garageRingLoaded = false;
        this.A.position.set(0, 0, 0);
        this.B.position.set(0, 0, 0);
        this.C.position.set(0, 0, 0);
    }
    async setRing(ringModel, oppositeRingType) {
        await this.ring.setRing(ringModel, oppositeRingType);
        this.update();
        this.scaleCones();
    }
    setRingCalibrationData(args) {
        this.ring.boneType = args.boneType;
        this.ring.refType = args.refType;
        this.ring.side = args.side;
        this.ring.viewTypeRx = args.viewType;
    }
    onRegSuccess(points) {
        this.A.position.copy(points[0].setZ(0));
        this.B.position.copy(points[1].setZ(0));
        this.C.position.copy(points[2].setZ(0));
        this.coneA.position.copy(this.A.position);
        this.coneB.position.copy(this.B.position);
        this.rotationPoint.position.copy(this.C.position);
        this.updateConeC(this.coneA.position, this.coneB.position);
        this.update();
        this.scaleCones();
    }
    onRegPoint(point) {
        super.onRegPoint(point);
        if (this._registeredPoints.length === 2) {
            this.updateEllipseParameters(this._registeredPoints[0], this._registeredPoints[1]);
        }
    }
    onRegMove(point) {
        switch (this._registeredPoints.length) {
            case 1:
                return this._shape.updateGeoPoints([...this._registeredPoints, point], 1);
            case 2:
                this._ellipseCurve.yRadius = this.getEllipseYRadius(point);
                if (isNaN(this._ellipseCurve.yRadius)) { //aggiungere limite raggio tODO
                    // TODO rendere più fluido e bloccare punto
                    this._ellipseCurve.yRadius = point.distanceTo(VectorUtils.projectOnVector(point, this._registeredPoints[0], this._registeredPoints[1]));
                }
                return this._shape.setGeoPoints(this._ellipseCurve.getPoints(this._curveComplexity));
        }
    }
    onRegValidation(points) {
        const radiusX = points[0].distanceTo(points[1]) / 2;
        const radiusY = this.getEllipseYRadius(points[2]);
        if (isNaN(radiusY) || radiusX <= radiusY || radiusX > 50) // 100 is plane width
            return 'calibrationEllipseRadiusError';
        return undefined;
    }
}
//# sourceMappingURL=CalibrationEllipse.js.map